import { Link } from 'react-router-dom'
import { 
  QrCode, 
  Smartphone, 
  Share2, 
  Palette, 
  BarChart3, 
  Download,
  ChevronRight,
  Check,
  Star,
  ChevronDown,
  User,
  Zap,
  Shield,
  Clock,
  Sparkles,
  ArrowRight,
  Play,
  Phone,
  Mail,
  MessageCircle
} from 'lucide-react'
import { useState } from 'react'
import Header from '../components/Header'
import Footer from '../components/Footer'
import { templates } from '../templates'
import '../templates/templates.css'
import './LandingPage.css'
import MobileCanvas from '../components/MobileCanvas'
import SEO from '../components/SEO'

export default function LandingPage() {
  const [openFaq, setOpenFaq] = useState(null)
  const [billingCycle, setBillingCycle] = useState('yearly')

  const features = [
    {
      icon: <Smartphone size={28} />,
      title: 'Mobile-Friendly vCard',
      description: 'Your profile looks perfect on any device. No app needed.'
    },
    {
      icon: <QrCode size={28} />,
      title: 'Instant QR Code',
      description: 'Scan to view your details instantly. Share anywhere.'
    },
    {
      icon: <Download size={28} />,
      title: 'Save Contact Button',
      description: 'Users can add you to their phone in one tap.'
    },
    {
      icon: <Palette size={28} />,
      title: 'Custom Themes',
      description: 'Choose from multiple professional designs.'
    },
    {
      icon: <Share2 size={28} />,
      title: 'Easy Sharing',
      description: 'Share via WhatsApp, SMS, Email or any platform.'
    },
    {
      icon: <BarChart3 size={28} />,
      title: 'Analytics Dashboard',
      description: 'See how many people viewed or scanned your QR.'
    }
  ]

  const workflowSteps = [
    {
      step: 1,
      title: 'Sign Up & Add Details',
      description: 'Create your account and fill in your contact information, social links, and services.',
      image: 'form',
      color: '#00A6FB'
    },
    {
      step: 2,
      title: 'Customize Your Card',
      description: 'Choose a theme, upload your photo, and personalize your digital vCard.',
      image: 'customize',
      color: '#10B981'
    },
    {
      step: 3,
      title: 'Get Your QR Code',
      description: 'Download your unique QR code and shareable link instantly.',
      image: 'qr',
      color: '#8B5CF6'
    },
    {
      step: 4,
      title: 'Share Everywhere',
      description: 'Print on cards, share online, or display at events. Track all views!',
      image: 'share',
      color: '#F59E0B'
    }
  ]

  const useCases = [
    { 
      icon: <img src="/assets/icon/bussines_icon.png" alt="Business" />, 
      title: 'Business Owners', 
      desc: 'Professional image for clients' 
    },
    { 
      icon: <img src="/assets/icon/freelancer_icon.png" alt="Freelancer" />, 
      title: 'Freelancers', 
      desc: 'Showcase portfolio & services' 
    },
    { 
      icon: <img src="/assets/icon/real_estate_icon.png" alt="Real Estate" />, 
      title: 'Real Estate', 
      desc: 'Share listings & contact easily' 
    },
    { 
      icon: <img src="/assets/icon/sales_team_icon.png" alt="Sales" />, 
      title: 'Sales Teams', 
      desc: 'Team cards with analytics' 
    }
  ]

  const plans = [
    {
      name: 'Starter',
      price: billingCycle === 'monthly' ? '₹9' : '₹99',
      period: billingCycle === 'monthly' ? '/month' : '/year',
      description: 'Perfect for individuals',
      features: [
        '1 Digital vCard',
        '1 Template',
        'QR Code Download',
        'Basic Analytics',
        'Unlimited Sharing'
      ],
      popular: false
    },
    {
      name: 'Pro',
      price: billingCycle === 'monthly' ? '₹29' : '₹199',
      period: billingCycle === 'monthly' ? '/month' : '/year',
      description: 'Most Popular Choice',
      features: [
        'All templates & themes',
        'Unlimited updates',
        'Advanced analytics',
        'Custom username',
        'Services & portfolio section',
        'Priority support'
      ],
      popular: true
    },
    {
      name: 'Business',
      price: billingCycle === 'monthly' ? '₹99' : '₹499',
      period: billingCycle === 'monthly' ? '/month' : '/year',
      description: 'For small teams',
      features: [
        'Up to 5 vCards',
        'Remove branding',
        'Employee profiles',
        'Lead capture form',
        'Team dashboard',
        'Premium support'
      ],
      popular: false
    }
  ]

  const testimonials = [
    {
      name: 'Aravind ',
      role: 'Real Estate Agent',
      text: 'I replaced 500 printed cards with one QR. My clients love how easy it is to save my contact.',
      rating: 5
    },
    {
      name: 'Priya Sharma',
      role: 'Makeup Artist',
      text: 'My clients save my number in one click. The portfolio section helps showcase my work beautifully.',
      rating: 5
    },
    {
      name: 'Karthik Raj',
      role: 'Freelance Designer',
      text: '₹9/month is nothing. I use it everywhere - on my portfolio, business cards, and social media.',
      rating: 5
    }
  ]

  const faqs = [
    {
      question: 'What is a digital vCard?',
      answer: 'A digital vCard is a modern version of your visiting card. It\'s an online profile with a unique link and QR code that contains your contact information, social links, services, and portfolio - all shareable instantly.'
    },
    {
      question: 'Do my clients need to install an app?',
      answer: 'No! Your vCard works on any smartphone browser. When someone scans your QR or clicks your link, they view your profile instantly - no app required.'
    },
    {
      question: 'Can I update my card anytime?',
      answer: 'Yes! Unlike printed cards, you can update your digital vCard anytime. Changes reflect instantly - no reprinting needed.'
    },
    {
      question: 'Can I cancel anytime?',
      answer: 'Absolutely. There\'s no lock-in period. You can cancel your subscription anytime from your dashboard.'
    },
    {
      question: 'Is it really affordable?',
      answer: 'Yes! At just ₹9/month or ₹99/year, it\'s India\'s most affordable digital card solution. Compare that to ₹200-500 every time you reprint paper cards!'
    }
  ]

  return (
    <div className="landing-page">
      <SEO 
        title="Create Digital vCard Online | Smart Visiting Card"
        description="Join 10,000+ professionals using Digital vCard. Create your smart digital visiting card with QR code, analytics, and instant sharing. Start for free!"
        keywords="digital vcard, smart visiting card, qr code business card, online business card, digital profile, electronic business card india"
      />
      <Header />
      
      {/* Hero Section - Enhanced */}
      <section className="hero">
        <div className="hero-bg">
          <div className="hero-gradient"></div>
          <div className="hero-grid"></div>
          <div className="hero-glow hero-glow-1"></div>
          <div className="hero-glow hero-glow-2"></div>
          <div className="hero-glow hero-glow-3"></div>
        </div>
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <div className="hero-badge">
                <Sparkles size={16} />
                <span>Join 10,000+ Professionals</span>
              </div>
              <h1 className="hero-title">
                Your Smart Digital<br />
                <span className="gradient-text">Business Card</span>
              </h1>
              <p className="hero-subtitle">
                Replace paper cards forever. Create a stunning digital vCard with QR code, 
                shareable link, and real-time analytics — all for just <strong>₹9/month</strong>.
              </p>
              <div className="hero-features-list">
                <div className="hero-feature">
                  <Check size={18} />
                  <span>No App Required</span>
                </div>
                <div className="hero-feature">
                  <Check size={18} />
                  <span>Update Anytime</span>
                </div>
                <div className="hero-feature">
                  <Check size={18} />
                  <span>Works Everywhere</span>
                </div>
              </div>
              <div className="hero-actions">
                <Link to="/signup" className="btn btn-primary btn-lg btn-glow">
                  Create Your vCard Free
                  <ArrowRight size={20} />
                </Link>
                <a href="#demo" className="btn btn-glass btn-lg">
                  <Play size={18} />
                  Watch Demo
                </a>
              </div>
              <div className="hero-trust">
                <div className="trust-avatars">
                  <div className="trust-avatar">A</div>
                  <div className="trust-avatar">P</div>
                  <div className="trust-avatar">K</div>
                  <div className="trust-avatar">+</div>
                </div>
                <div className="trust-text">
                  <div className="trust-rating">
                    <Star size={14} fill="#FBBF24" color="#FBBF24" />
                    <Star size={14} fill="#FBBF24" color="#FBBF24" />
                    <Star size={14} fill="#FBBF24" color="#FBBF24" />
                    <Star size={14} fill="#FBBF24" color="#FBBF24" />
                    <Star size={14} fill="#FBBF24" color="#FBBF24" />
                    <span>4.9/5</span>
                  </div>
                  <p>Trusted by 10,000+ users</p>
                </div>
              </div>
            </div>
            <div className="hero-visual">
              <div className="phone-mockup">
                <MobileCanvas>
                  <div className="vcard-demo">
                    <div className="vcard-demo-header">
                      <div className="vcard-demo-avatar">
                        <User size={32} />
                      </div>
                      <h4>Rahul Sharma</h4>
                      <p>Business Consultant</p>
                    </div>
                    <div className="vcard-demo-buttons">
                      <button>📞 Call</button>
                      <button>💬 WhatsApp</button>
                      <button>✉️ Email</button>
                    </div>
                    <div className="vcard-demo-section">
                      <span>About</span>
                      <p>Helping businesses grow with strategic consulting and marketing solutions.</p>
                    </div>
                    <div className="vcard-demo-section">
                      <span>Services</span>
                      <div className="vcard-demo-services">
                        <span>Business Strategy</span>
                        <span>Marketing</span>
                        <span>Consulting</span>
                      </div>
                    </div>
                  </div>
                </MobileCanvas>
                <div className="floating-elements">
                  <div className="floating-qr">
                    <QrCode size={60} />
                    <span>Scan Me!</span>
                  </div>
                  <div className="floating-stat floating-stat-1">
                    <span className="stat-icon">👁️</span>
                    <span>156 views</span>
                  </div>
                  <div className="floating-stat floating-stat-2">
                    <span className="stat-icon">📱</span>
                    <span>42 saves</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Brands/Trust Section */}
      <section className="brands-section">
        <div className="container">
          <p className="brands-label">Trusted by professionals from</p>
          <div className="brands-list">
            <span>🏢 Startups</span>
            <span>💼 Corporates</span>
            <span>🎨 Freelancers</span>
            <span>🏠 Real Estate</span>
            <span>🩺 Healthcare</span>
            <span>📚 Education</span>
          </div>
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="problem-section section">
        <div className="container">
          <div className="problem-grid">
            <div className="problem-card">
              <div className="problem-icon">❌</div>
              <h3>Paper Cards Are Outdated</h3>
              <ul>
                <li>Gets lost or thrown away</li>
                <li>Expensive to reprint</li>
                <li>Can't update details</li>
                <li>No way to track engagement</li>
              </ul>
            </div>
            <div className="vs-badge">VS</div>
            <div className="solution-card">
              <div className="solution-icon">✨</div>
              <h3>Digital vCard Is Smart</h3>
              <ul>
                <li>Always accessible via QR</li>
                <li>Update anytime for free</li>
                <li>Just ₹9/month forever</li>
                <li>Track views & engagement</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - Visual Workflow */}
      <section className="workflow-section section section-light" id="demo">
        <div className="container">
          <div className="section-header">
            <span className="section-label">How It Works</span>
            <h2 className="section-title">Create Your vCard in Minutes</h2>
            <p className="section-subtitle">Simple 4-step process to go digital</p>
          </div>
          
          <div className="workflow-grid">
            {workflowSteps.map((item) => (
              <div key={item.step} className="workflow-card">
                <div className="workflow-step" style={{ background: item.color }}>
                  {item.step}
                </div>
                <div className="workflow-visual" style={{ borderColor: item.color }}>
                  {item.image === 'form' && (
                    <div className="workflow-icon-container">
                      <User size={48} color={item.color} />
                      <div className="workflow-mini-icons">
                        <span>📧</span>
                        <span>📱</span>
                        <span>🔗</span>
                      </div>
                    </div>
                  )}
                  {item.image === 'customize' && (
                    <div className="workflow-icon-container">
                      <Palette size={48} color={item.color} />
                      <div className="workflow-mini-icons">
                        <span>🎨</span>
                        <span>📸</span>
                        <span>✨</span>
                      </div>
                    </div>
                  )}
                  {item.image === 'qr' && (
                    <div className="workflow-icon-container">
                      <QrCode size={48} color={item.color} />
                      <div className="workflow-mini-icons">
                        <span>📥</span>
                        <span>🔗</span>
                        <span>📋</span>
                      </div>
                    </div>
                  )}
                  {item.image === 'share' && (
                    <div className="workflow-icon-container">
                      <Share2 size={48} color={item.color} />
                      <div className="workflow-mini-icons">
                        <span>📲</span>
                        <span>💬</span>
                        <span>📊</span>
                      </div>
                    </div>
                  )}
                </div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            ))}
          </div>

          <div className="workflow-cta">
            <Link to="/signup" className="btn btn-primary btn-lg">
              Start Creating Now
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="use-cases section">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Who Is It For</span>
            <h2 className="section-title">Perfect for Every Professional</h2>
          </div>
          <div className="use-cases-grid">
            {useCases.map((item, index) => (
              <div key={index} className="use-case-card">
                <span className="use-case-icon">{item.icon}</span>
                <h4>{item.title}</h4>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Templates Showcase */}
      <section className="templates-section section" id="templates">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Templates</span>
            <h2 className="section-title">Choose Your Perfect Look</h2>
            <p className="section-subtitle">Beautiful, professional designs to match your brand</p>
          </div>
          
          <div className="templates-grid">
            {['1.jpg', '2.jpg', '3.jpg', '4.jpg', '5.jpg', '6.jpg'].map((img, index) => (
              <div key={index} className="template-preview-card">
                <MobileCanvas className="template-mobile-canvas">
                  <img 
                    src={`/assets/${img}`} 
                    alt={`Template ${index + 1}`} 
                    className="template-image"
                    loading="lazy"
                  />
                </MobileCanvas>
              </div>
            ))}
          </div>

          <div className="templates-cta">
            <Link to="/signup" className="btn btn-primary btn-lg">
              Try All Templates Free
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="features section section-dark" id="features">
        <div className="container">
          <div className="section-header">
            <span className="section-label light">Features</span>
            <h2 className="section-title">Everything You Need</h2>
            <p className="section-subtitle">Powerful features to make your digital presence stand out</p>
          </div>
          
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">
                  {feature.icon}
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="pricing section" id="pricing">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Pricing</span>
            <h2 className="section-title">Simple, Affordable Pricing</h2>
            <p className="section-subtitle">Choose the perfect plan for your needs</p>
          </div>
          
          {/* Billing Toggle */}
          <div className="billing-toggle">
            <button 
              className={billingCycle === 'monthly' ? 'active' : ''}
              onClick={() => setBillingCycle('monthly')}
            >
              Monthly
            </button>
            <button 
              className={billingCycle === 'yearly' ? 'active' : ''}
              onClick={() => setBillingCycle('yearly')}
            >
              Yearly
              <span className="save-badge">Save 20%</span>
            </button>
          </div>

          <div className="pricing-grid">
            {plans.map((plan, index) => (
              <div key={index} className={`pricing-card ${plan.popular ? 'popular' : ''}`}>
                {plan.popular && <div className="popular-badge">Most Popular</div>}
                <h3>{plan.name}</h3>
                <p className="plan-description">{plan.description}</p>
                <div className="plan-price">
                  <span className="price">{plan.price}</span>
                  <span className="period">{plan.period}</span>
                </div>
                <ul className="plan-features">
                  {plan.features.map((feature, i) => (
                    <li key={i}>
                      <Check size={18} />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link 
                  to="/signup" 
                  className={`btn ${plan.popular ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ width: '100%' }}
                >
                  Get Started
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials section section-light">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Testimonials</span>
            <h2 className="section-title">Loved by Professionals</h2>
            <p className="section-subtitle">See what our users say about Digital vCard</p>
          </div>
          
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card">
                <div className="testimonial-rating">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={18} fill="#FBBF24" color="#FBBF24" />
                  ))}
                </div>
                <p className="testimonial-text">"{testimonial.text}"</p>
                <div className="testimonial-author">
                  <div className="author-avatar">{testimonial.name[0]}</div>
                  <div>
                    <strong>{testimonial.name}</strong>
                    <span>{testimonial.role}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq section" id="faq">
        <div className="container container-sm">
          <div className="section-header">
            <span className="section-label">FAQ</span>
            <h2 className="section-title">Frequently Asked Questions</h2>
          </div>
          
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className={`faq-item ${openFaq === index ? 'open' : ''}`}
              >
                <button 
                  className="faq-question"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span>{faq.question}</span>
                  <ChevronDown size={20} />
                </button>
                <div className="faq-answer">
                  <p>{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>



      {/* Product Hunt Launch Support */}
      <section className="section section-light producthunt-section">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Support Us</span>
            <h2 className="section-title">We are live on Product Hunt!</h2>
            <p className="section-subtitle">Check out our launch and show some love ❤️</p>
          </div>
          <div className="producthunt-frame-wrapper">
            <iframe 
              className="producthunt-frame"
              title="Digital vCard on Product Hunt"
              src="https://cards.producthunt.com/cards/products/1136290" 
              width="100%"
              height="405" 
              frameBorder="0" 
              scrolling="no" 
              loading="lazy"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-final section">
        <div className="container">
          <div className="cta-card">
            <div className="cta-content">
              <div className="cta-badge">🎯 Limited Time Offer</div>
              <h2 className="cta-title">
                Start Your Digital Journey<br />
                <span>for Just ₹9/month</span>
              </h2>
              <p className="cta-subtitle">
                Join 10,000+ professionals who ditched paper cards forever. 
                Create your stunning digital vCard in under 2 minutes.
              </p>
              <div className="cta-features">
                <div className="cta-feature">
                  <Check size={18} />
                  <span>Free 7-day trial</span>
                </div>
                <div className="cta-feature">
                  <Check size={18} />
                  <span>No credit card needed</span>
                </div>
                <div className="cta-feature">
                  <Check size={18} />
                  <span>Cancel anytime</span>
                </div>
              </div>
              <div className="cta-actions">
                <Link to="/signup" className="btn btn-primary btn-xl">
                  <Zap size={20} />
                  Create My vCard Now
                </Link>
              </div>
              <div className="cta-trust">
                <div className="cta-stars">
                  <Star size={16} fill="#FBBF24" color="#FBBF24" />
                  <Star size={16} fill="#FBBF24" color="#FBBF24" />
                  <Star size={16} fill="#FBBF24" color="#FBBF24" />
                  <Star size={16} fill="#FBBF24" color="#FBBF24" />
                  <Star size={16} fill="#FBBF24" color="#FBBF24" />
                </div>
                <span>Rated 4.9/5 by 500+ users</span>
              </div>
            </div>
            <div className="cta-visual">
              <div className="cta-phone">
                <QrCode size={100} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}
